const VIDEO_CONFIG = {
    SELECTORS: {
        DESKTOP_VIDEO: '.js-hero-video--desktop',
        MOBILE_VIDEO: '.js-hero-video--mobile',
        TOGGLE_BUTTON: '.hero-video__toggle'
    },
    BREAKPOINTS: {
        DESKTOP: '768px'
    },
    SYNC_INTERVAL: 1000,
    ICONS: {
        PLAY: '<svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none"><path d="M15.992 11.3685L10.1562 7.47793C9.65098 7.14951 8.99414 7.5032 8.99414 8.10951V15.8906C8.99414 16.4969 9.67625 16.8506 10.1562 16.5221L15.992 12.6316C16.4468 12.3285 16.4468 11.6716 15.992 11.3685Z" fill="#1a67b2"/></svg>',
        PAUSE: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="22" viewBox="0 0 20 22" fill="none"><path fill-rule="evenodd" clip-rule="evenodd" d="M3.57143 0C5.54422 0 7.14286 1.59864 7.14286 3.57143V18.3333C7.14286 20.3061 5.54422 21.9048 3.57143 21.9048C1.59864 21.9048 0 20.3061 0 18.3333V3.57143C0 1.59864 1.59864 0 3.57143 0Z" fill="#1A67B2"/><path fill-rule="evenodd" clip-rule="evenodd" d="M16.3947 0C18.3675 0 20.0001 1.59864 20.0001 3.57143V18.3333C20.0001 20.3061 18.3675 21.9048 16.3947 21.9048C14.4219 21.9048 12.8232 20.3061 12.8232 18.3333V3.57143C12.8232 1.59864 14.4219 0 16.3947 0Z" fill="#1A67B2"/></svg>'
    },
    LABELS: {
        PLAY: 'Play video',
        PAUSE: 'Pause video'
    },
    VIDEO_ATTRIBUTES: {
        muted: true,
        playsInline: true,
        loop: true,
        autoplay: true
    }
};

class HeroVideo {
    constructor() {
        this.desktopVideo = document.querySelector(VIDEO_CONFIG.SELECTORS.DESKTOP_VIDEO);
        this.mobileVideo = document.querySelector(VIDEO_CONFIG.SELECTORS.MOBILE_VIDEO);
        this.toggleButton = document.querySelector(VIDEO_CONFIG.SELECTORS.TOGGLE_BUTTON);

        if (!this.toggleButton || (!this.desktopVideo && !this.mobileVideo)) return;

        this.state = {
            isPaused: false,
            savedTime: 0,
            syncInterval: null,
            resizeTimeout: null
        };

        this.prevIsDesktop = this.isDesktop();
        this.lastResizeAt = 0;
        this.MIN_RESIZE_INTERVAL = 250;

        this.init();
    }

    init() {
        this.setupVideos();
        this.setupEventListeners();
        this.startVideoSync();
        this.updateButtonState(false);
        this.playActiveVideo();
    }

    setupVideos() {
        [this.desktopVideo, this.mobileVideo].forEach(video => {
            if (!video) return;
            Object.entries(VIDEO_CONFIG.VIDEO_ATTRIBUTES).forEach(([key, value]) => {
                video[key] = value;
            });
        });
    }

    isDesktop() {
        return window.matchMedia(`(min-width: ${VIDEO_CONFIG.BREAKPOINTS.DESKTOP})`).matches;
    }

    getActiveVideo() {
        return this.isDesktop()
            ? (this.desktopVideo || this.mobileVideo)
            : (this.mobileVideo || this.desktopVideo);
    }

    getInactiveVideo() {
        return this.getActiveVideo() === this.desktopVideo
            ? this.mobileVideo
            : this.desktopVideo;
    }

    async safePlayVideo(video) {
        if (!video?.play) return;
        try {
            await video.play();
        } catch (error) {
            console.warn('Video autoplay failed:', error);
        }
    }

    safePauseVideo(video) {
        if (video?.pause) video.pause();
    }

    safeSetTime(video, time) {
        if (!video) return;
        try {
            video.currentTime = time;
        } catch (error) {
            console.warn('Failed to set video time:', error);
        }
    }

    updateButtonState(isPaused) {
        if (!this.toggleButton) return;

        this.toggleButton.innerHTML = isPaused ? VIDEO_CONFIG.ICONS.PLAY : VIDEO_CONFIG.ICONS.PAUSE;
        this.toggleButton.setAttribute('aria-pressed', isPaused ? 'true' : 'false');
        this.toggleButton.setAttribute('aria-label', isPaused ? VIDEO_CONFIG.LABELS.PLAY : VIDEO_CONFIG.LABELS.PAUSE);
    }

    startVideoSync() {
        if (this.state.syncInterval) return;

        this.state.syncInterval = setInterval(() => {
            if (this.state.isPaused) return;

            const activeVideo = this.getActiveVideo();
            const inactiveVideo = this.getInactiveVideo();

            if (!activeVideo || !inactiveVideo) return;

            const currentTime = Number(activeVideo.currentTime);
            if (!Number.isNaN(currentTime)) {
                this.safeSetTime(inactiveVideo, currentTime);
            }
        }, VIDEO_CONFIG.SYNC_INTERVAL);
    }

    stopVideoSync() {
        if (this.state.syncInterval) {
            clearInterval(this.state.syncInterval);
            this.state.syncInterval = null;
        }
    }

    handleResize(force = false) {
        const now = Date.now();

        if (!force && (now - this.lastResizeAt) < this.MIN_RESIZE_INTERVAL) {
            return;
        }

        const nowIsDesktop = this.isDesktop();
        if (!force && nowIsDesktop === this.prevIsDesktop) {
            this.lastResizeAt = now;
            return;
        }

        this.prevIsDesktop = nowIsDesktop;
        this.lastResizeAt = now;

        const activeVideo = this.getActiveVideo();
        const inactiveVideo = this.getInactiveVideo();

        if (!activeVideo) return;

        if (this.state.isPaused) {
            if (inactiveVideo) this.safeSetTime(inactiveVideo, activeVideo.currentTime || 0);
        } else {
            if (inactiveVideo) {
                this.safePauseVideo(inactiveVideo);
                this.safeSetTime(inactiveVideo, activeVideo.currentTime || 0);
            }
            this.safePlayVideo(activeVideo);
        }

        this.updateButtonState(this.state.isPaused);
    }

    playActiveVideo() {
        const activeVideo = this.getActiveVideo();
        if (activeVideo) this.safePlayVideo(activeVideo);
    }

    setupEventListeners() {
        this.toggleButton.addEventListener('click', () => {
            const activeVideo = this.getActiveVideo();
            if (!activeVideo) return;

            if (!this.state.isPaused) {
                this.state.savedTime = activeVideo.currentTime || 0;
                this.safePauseVideo(activeVideo);
                this.safePauseVideo(this.getInactiveVideo());
                this.state.isPaused = true;
            } else {
                this.safeSetTime(activeVideo, this.state.savedTime);
                this.safeSetTime(this.getInactiveVideo(), this.state.savedTime);
                this.safePlayVideo(activeVideo);
                this.state.isPaused = false;
            }

            this.updateButtonState(this.state.isPaused);
        });

        window.addEventListener('resize', () => {
            if (this.state.resizeTimeout) return;

            this.state.resizeTimeout = requestAnimationFrame(() => {
                this.state.resizeTimeout = null;
                this.handleResize(false);
            });
        });

        window.addEventListener('orientationchange', () => {
            setTimeout(() => this.handleResize(true), 120);
        });

        document.addEventListener('visibilitychange', () => {
            if (!document.hidden) {
                setTimeout(() => this.handleResize(true), 80);
            }
        });

        [this.desktopVideo, this.mobileVideo].forEach(video => {
            if (!video) return;

            video.addEventListener('play', () => {
                this.state.isPaused = false;
                this.updateButtonState(false);
            });

            video.addEventListener('pause', () => {
                this.state.isPaused = true;
                this.updateButtonState(true);
            });
        });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    new HeroVideo();
});
